<template>
  <sticky-sidebar v-if="!isMobile" width="272px">
    <div class="container pl-2">
      <BackToDocsSection :is-mobile="isMobile" text="View All docs" />
      <div v-if="sidebar" class="section-title py-2 px-4 font-semibold uppercase not-italic text-base">
        <list-title :label-key="sidebar.key" :tag="sidebar?.tag" />
      </div>
      <Menu v-if="sidebar" :items="sidebar.children" />
    </div>
  </sticky-sidebar>

  <div v-else class="fixed top-[52.8px] z-[999] overflow-y-auto w-full">
    <div class="p-0 h-full">
      <ExpansionPanel v-model:open="openExpansionPanel" :title="sidebar?.key">
        <BackToDocsSection text="Back To All docs" :is-mobile="isMobile" />
        <Menu v-if="sidebar" :items="sidebar?.children" />
      </ExpansionPanel>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SidebarData } from '@bifrost-dev-docs-mfe/common/types/api';
import { nextTick, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { pathMatchesAnyChild, pathMatchesPreviousPath } from '@bifrost-dev-docs-mfe/common/types/sidebar';
import { apiClient } from '@/api';
import StickySidebar from '@/components/widgets/StickySidebar.vue';
import Menu from '@/components/navigation/left-sidebar/Menu.vue';
import ExpansionPanel from '@/components/navigation/left-sidebar/mobile-version/ExpansionPanel.vue';
import BackToDocsSection from '@/components/navigation/left-sidebar/BackToDocsSection.vue';
import ListTitle from '@/components/navigation/left-sidebar/ListTitle.vue';

const props = defineProps({ isMobile: { type: Boolean, required: true } });

const sidebar = ref<SidebarData | null>(null);
const openExpansionPanel = ref<boolean>(false);

const i18n = useI18n();
const route = useRoute();

function toggleExpansionPanel() {
  if (props.isMobile) {
    openExpansionPanel.value = false;
  }
}

watchEffect(onCleanup => {
  toggleExpansionPanel();
  const { locale } = i18n;
  const { path, query } = route;

  let cancelled = false;
  onCleanup(() => {
    cancelled = true;
  });

  const load = async () => {
    if (sidebar.value && pathMatchesAnyChild(sidebar.value, path) && pathMatchesPreviousPath(sidebar.value, path, '')) {
      // Skip loading sidebar if the current sidebar is already the correct sidebar
      return;
    }

    // Remove leading slash
    const pathWithoutSlash = path.slice(1);

    const source: string = query.source ? (query.source as string) : '';

    const sidebarData = await apiClient.getSidebar(locale.value, pathWithoutSlash, source);

    if (!cancelled) {
      sidebar.value = sidebarData;
    }
  };

  // This is to solve the infinite call problem in case of not found url
  nextTick(load, 0);
});
</script>

<style scoped lang="scss">
.section-title {
  color: var(--vvd-color-cta-600);
}
</style>
